import React, { useState, useEffect, useMemo } from "react";
import classNames from "classnames";
import { Link } from "react-router-dom";
import { MediaQuery } from "@outplayed/responsive";
import ChampionCounter from "@ugg/shared/components/common/Tooltips/ChampionCounter";
import { getRiotAssetsContext } from "@outplayed/riot-assets";
import { round } from "@ugg/shared/utils/math";
import { useQueryString } from "@ugg/shared/hooks/use-query-string";
import { buildQueryParams } from "@ugg/shared/utils/url-query-params";
import { normalizeRole } from "@ugg/shared/utils/role-helpers";
import { useValidateQueryParams } from "@ugg/shared/query-params/params-helpers";
import { validParams } from "@ugg/shared/query-params/valid-params";
import { getChampionBuildUrl, getChampionCountersUrl } from "@ugg/shared/routes/app-routes";
import { CHAMPION_PAGES } from "@ugg/shared/pages/champion-pages";
import { ReactComponent as ChevronRight } from "@ugg/shared/assets/svg/chevron-right.svg";
import { BadAgainstChampion } from "@ugg/shared/api/data-parser/tier-lists/tier-list";

interface AgainstItemProps {
  params: {
    idx: number;
    displayWinRate: number;
    displayWinRateMini: number;
    isGrayscale: boolean;
    curChampionId: number;
    secChampionId: number;
    datum: {
      type?: string;
      champion_id: number;
      wins: number;
      matches: number;
      win_rate: number;
      sec_win_rate: number;
    };
    role: string;
    dim: number;
  };
}

const AgainstItem = (props: AgainstItemProps) => {
  const { getNormalizedChampionName } = getRiotAssetsContext();
  const validateQueryParams = useValidateQueryParams(validParams);
  const { isGrayscale, secChampionId, role, dim } = props.params;

  const secChampionName = getNormalizedChampionName(secChampionId);
  const params = useQueryString();
  const currentFilters = { ...params };
  const validatedParams = validateQueryParams(CHAMPION_PAGES.OVERVIEW, currentFilters);
  const pathname = getChampionBuildUrl(secChampionName, { role: normalizeRole(role) });

  return (
    <Link
      className={classNames({ ["grayscale-[100%] opacity-[0.3]"]: isGrayscale })}
      to={{
        pathname,
        search: buildQueryParams(validatedParams),
      }}
    >
      <div
        className={`
        _champion-face relative w-[24px] h-[24px] rounded-[3px] max-xs:w-[18px] max-xs:h-[18px] overflow-hidden
        [&>div]:absolute [&>div]:top-[50%] [&>div]:left-[50%] [&>div]:translate-x-[-50%] 
        [&>div]:translate-y-[-50%] [&>div]:scale-[1.1]
      `}
      >
        <ChampionCounter dim={dim} data={props.params} />
      </div>
    </Link>
  );
};

const SeeMoreCounters = (props: { championId: number; role?: string }) => {
  const { getNormalizedChampionName } = getRiotAssetsContext();
  const params = useQueryString();
  const { championId, role } = props;
  const newParams = { ...params, ...(role ? { role: normalizeRole(role) } : {}) };

  return (
    <Link
      to={{
        pathname: getChampionCountersUrl(getNormalizedChampionName(championId)),
        search: buildQueryParams(newParams),
      }}
    >
      <div className="flex items-center justify-center w-[20px] h-[20px] rounded-full border-[1px] border-lavender-400">
        <ChevronRight width={7} />
      </div>
    </Link>
  );
};

interface AgainstProps {
  data: BadAgainstChampion[];
  championId: number;
  role: string;
  dim: number;
  showCount: number;
  showSeeMore?: boolean;
}

export const Against = (props: AgainstProps) => {
  const { data, championId, role, dim, showCount, showSeeMore } = props;

  const showMax = showCount || 7;
  const champs = useMemo(() => {
    const list = [];
    for (let i = 0; i < showMax; i++) {
      if (data.length > i) {
        const curData = data[i];
        const secWinRate = curData.opp_win_rate;
        const displayWinRate = round(secWinRate, 1);
        const displayWinRateMini = round(secWinRate);
        const isGrayscale = displayWinRate < 50;

        const params = {
          datum: { ...curData, sec_win_rate: secWinRate },
          idx: i,
          isGrayscale,
          displayWinRate,
          displayWinRateMini,
          curChampionId: championId,
          secChampionId: curData.champion_id,
          role,
          dim,
        };

        list.push(<AgainstItem key={i} params={params} />);
      } else {
        list.push(
          <div
            key={i}
            className="relative w-[24px] h-[24px] rounded-[3px] max-xs:w-[18px] max-xs:h-[18px]"
            style={{ width: `${dim}px`, height: `${dim}px` }}
          />,
        );
      }
    }

    return list;
  }, [data]);

  return (
    <div className="flex items-center gap-[6px] xxxs:max-xxs:gap-[4px]">
      {champs}
      {showSeeMore && (
        <MediaQuery min="MOBILE_LARGE" max="DESKTOP_LARGE">
          {(className) => (
            <div className={classNames(className, "ml-[4px]")}>
              <SeeMoreCounters championId={championId} role={role} />
            </div>
          )}
        </MediaQuery>
      )}
    </div>
  );
};
